import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { 
  Card, CardContent, Button, Box, TextField, 
  CircularProgress, Alert, Typography, Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Title from '../../components/common/Title';
import AuditLogTable from '../../components/tables/AuditLogTable';
import { api } from '../../services/api';

// Интерфейс для записи аудита
interface AuditLog {
  id: number;
  userId: number;
  action: string;
  createdAt: string;
}

interface AuditResponse {
  page: number;
  maxPages: number;
  items: AuditLog[];
}

// API функция для получения данных аудита
const fetchAuditLogs = async (page: number, limit: number, search?: string): Promise<AuditResponse> => {
  const params: any = { page, limit };
  if (search) {
    params.search = search;
  }

  const response = await api.get('/api/v1/admin-panel/audit-logs', {
    params,
    headers: {
      'x-hasura-admin-secret': localStorage.getItem('hasura_admin_secret') || 'admin_secret_key'
    }
  });

  return response.data.data;
};

const AuditPage: React.FC = () => {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Используем React Query для получения данных
  const { data, isLoading, error, refetch } = useQuery(
    ['audit-logs', page + 1, rowsPerPage, searchQuery],
    () => fetchAuditLogs(page + 1, rowsPerPage, searchQuery || undefined),
    {
      keepPreviousData: true,
      staleTime: 10000,
    }
  );
  
  // Обработчик поиска
  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(0);
    refetch();
  };
  
  // Преобразование данных из API в формат для таблицы
  const formatDataForTable = (): any[] => {
    if (!data?.items) return [];
    
    return data.items.map((item) => ({
      id: item.id,
      user_id: item.userId,
      action: item.action,
      created_at: item.createdAt
    }));
  };

  return (
    <Card>
      <CardContent>
        <Title title="Журнал аудита" />

        <Box component="form" onSubmit={handleSearch} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <TextField
            label="Поиск"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1, mr: 2 }}
            placeholder="Поиск по действию"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            type="submit"
          >
            Поиск
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Ошибка при загрузке данных: {error instanceof Error ? error.message : 'Неизвестная ошибка'}
          </Alert>
        )}
        
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {!isLoading && (!data?.items || data.items.length === 0) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Записи не найдены
          </Alert>
        )}
        
        {!isLoading && data?.items && data.items.length > 0 && (
          <AuditLogTable 
            data={formatDataForTable()} 
            loading={isLoading} 
            pagination={{
              page,
              rowsPerPage,
              count: data.maxPages * rowsPerPage,
              onPageChange: (newPage) => setPage(newPage),
              onRowsPerPageChange: (newRowsPerPage) => {
                setRowsPerPage(newRowsPerPage);
                setPage(0);
              }
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default AuditPage; 